import identifierSchema, {
  optionalIdentifierSchema,
} from "@repo/components/src/utils/yup/identifierSchema";
import {
  TaxProfileFieldKeyEnum,
  TaxRegimeEnum,
} from "@repo/graphql/gql/graphql";
import * as yup from "yup";

import { findField, hasField } from "./fields";
import type {
  RegimeOption,
  TaxProfileFieldData,
  TaxProfileFormValues,
} from "./types";

/**
 * How the acknowledgment checkbox is required: never, always (a payer
 * confirming its data), or only for a regime whose highlights the creator
 * must read (the regime asks for it by having highlights).
 */
export type AcknowledgmentRule =
  { kind: "none" } | { kind: "always"; message: string } | { kind: "regime" };

export const REGIME_ACKNOWLEDGMENT_MESSAGE =
  "Confirma que entiendes cómo se emitirán tus documentos";

const REGIME_MESSAGE = "Selecciona un régimen tributario";

const REQUIRED_MESSAGES: Partial<Record<TaxProfileFieldKeyEnum, string>> = {
  [TaxProfileFieldKeyEnum.BusinessActivity]: "Ingresa tu giro",
};

const DEFAULT_REQUIRED_MESSAGE = "Ingresa este dato";

const textSchema = (field: TaxProfileFieldData | undefined) =>
  field?.required
    ? yup
        .string()
        .required(REQUIRED_MESSAGES[field.key] ?? DEFAULT_REQUIRED_MESSAGE)
    : yup.string().default("");

const emailSchema = (field: TaxProfileFieldData | undefined) => {
  const email = yup.string().email("Ingresa un correo válido");
  return field?.required
    ? email.required(DEFAULT_REQUIRED_MESSAGE)
    : email.default("");
};

const acknowledgmentSchema = (
  rule: AcknowledgmentRule,
  regimes: ReadonlyArray<RegimeOption>,
) => {
  if (rule.kind === "always") {
    return yup
      .boolean()
      .oneOf([true], rule.message)
      .required(rule.message)
      .default(false);
  }
  if (rule.kind === "regime") {
    const codes = regimes
      .filter((regime) => regime.highlights.length > 0)
      .map((regime) => regime.code);
    return yup
      .boolean()
      .default(false)
      .when("taxRegime", {
        is: (value: TaxRegimeEnum | undefined) =>
          !!value && codes.includes(value),
        then: (schema) =>
          schema
            .oneOf([true], REGIME_ACKNOWLEDGMENT_MESSAGE)
            .required(REGIME_ACKNOWLEDGMENT_MESSAGE),
      });
  }
  return yup.boolean().default(false);
};

/**
 * The schema of the active form. A field the form doesn't include isn't
 * validated: it keeps its default and is never sent (see `buildTaxProfileInput`).
 */
export const buildTaxProfileSchema = (
  fields: ReadonlyArray<TaxProfileFieldData>,
  regimes: ReadonlyArray<RegimeOption>,
  acknowledgment: AcknowledgmentRule,
): yup.ObjectSchema<TaxProfileFormValues> => {
  const regimeCodes = regimes.map((regime) => regime.code);
  return yup.object({
    taxId: hasField(fields, TaxProfileFieldKeyEnum.TaxId)
      ? identifierSchema("Ingresa tu identificador tributario")
      : optionalIdentifierSchema,
    taxRegime: hasField(fields, TaxProfileFieldKeyEnum.TaxRegime)
      ? yup
          .mixed<TaxRegimeEnum>()
          .oneOf(regimeCodes, REGIME_MESSAGE)
          .required(REGIME_MESSAGE)
      : yup.mixed<TaxRegimeEnum>().optional(),
    legalName: textSchema(findField(fields, TaxProfileFieldKeyEnum.LegalName)),
    businessActivity: textSchema(
      findField(fields, TaxProfileFieldKeyEnum.BusinessActivity),
    ),
    email: emailSchema(findField(fields, TaxProfileFieldKeyEnum.Email)),
    addressLine: textSchema(
      findField(fields, TaxProfileFieldKeyEnum.AddressLine),
    ),
    isLargeTaxpayer: yup.boolean().default(false),
    isMunicipalWithholdingAgent: yup.boolean().default(false),
    acknowledged: acknowledgmentSchema(acknowledgment, regimes),
  });
};
